import { UserService } from "../../services";

import Notifications from "./settings-notifications";
import Vue from "vue";
import * as _ from "lodash-es";

/**
 * @typedef {Object} NotificationChannel
 * @property {string} channel_id
 * @property {string} channel_name
 * @property {boolean} is_working
 */

/**
 * @typedef {Object} NotificationItem
 * @property {string} notification_id
 * @property {string} notification_name
 * @property {string[]} channels
 */

/**
 * @typedef {Object} NotificationSection
 * @property {string} section_id
 * @property {string} section_name
 * @property {NotificationItem[]} notifications
 */

/**
 * @typedef {Object} SettingNotify
 * @property {(NotificationChannel[]|NotificationSection[])} settings
 * @property {NotificationChannel[]} [channels_types]
 */

function resetState() {
  return {
    avatar_is_loading: false,
    text_field_is_loading: false,
    password_is_updating: false,

    push_event_types_are_loading: false,

    push_message: false,
    push_journal: false,
    push_contract: false,

    listNotify: [],
    settingNotify: {
      settings: [],
      channels_types: [],
    },
    typeSettingNotify: null,
    push_offer: false,
    push_avatar: false,
    push_rest: false,
  };
}

export default {
  namespaced: true,

  state: resetState(),

  getters: {
    isAvatarLoading({ avatar_is_loading }) {
      return avatar_is_loading;
    },

    isTextFieldLoading({ text_field_is_loading }) {
      return text_field_is_loading;
    },

    isPasswordUpdating({ password_is_updating }) {
      return password_is_updating;
    },

    arePushEventTypesLoading({ push_event_types_are_loading }) {
      return push_event_types_are_loading;
    },

    getPushSettingFor(state) {
      return function name(name) {
        return state[`push_${name}`];
      };
    },

    getListNotify(state) {
      return state.listNotify;
    },

    getSettingNotify(state) {
      return state.settingNotify;
    },

    getTypeSettingNotify(state) {
      return state.typeSettingNotify;
    },
  },

  mutations: {
    SET_AVATAR_IS_LOADING(state, value) {
      state.avatar_is_loading = value;
    },

    SET_TEXT_FIELD_IS_LOADING(state, value) {
      state.text_field_is_loading = value;
    },

    SET_PASSWORD_IS_UPDATING(state, value) {
      state.password_is_updating = value;
    },

    SET_PUSH_MESSAGE(state, value) {
      state.push_message = value;
    },

    SET_PUSH_JOURNAL(state, value) {
      state.push_journal = value;
    },

    SET_PUSH_CONTRACT(state, value) {
      state.push_contract = value;
    },

    SET_LIST_NOTIFY(state, value) {
      state.listNotify = value;
    },

    SET_SETTING_NOTIFY(state, value) {
      state.typeSettingNotify = value.simple_settings;

      const newSettings = value.settings;
      const newChannelsTypes = value.channels_types || state.settingNotify.channels_types;

      const isSameStructure =
        _.isArray(newSettings) === _.isArray(state.settingNotify.settings) &&
        newSettings.length > 0 &&
        state.settingNotify.settings.length > 0 &&
        _.has(newSettings[0], "channel_id") === _.has(state.settingNotify.settings[0], "channel_id");

      const settingsChanged = !_.isEqual(newSettings, state.settingNotify.settings);
      const channelsTypesChanged = !_.isEqual(newChannelsTypes, state.settingNotify.channels_types);

      // Обновляем только при изменениях или смене типа структуры
      if (!isSameStructure || settingsChanged || channelsTypesChanged) {
        Vue.set(state.settingNotify, "settings", _.cloneDeep(newSettings));

        if (newChannelsTypes) {
          Vue.set(state.settingNotify, "channels_types", _.cloneDeep(newChannelsTypes));
        }
      }
    },

    SET_ITEM_SETTING_NOTIFY(state, value) {
      state.settingNotify.settings = state.settingNotify.settings.map((section) => {
        if (section.section_id !== value.section_id) return section;

        return {
          ...section,
          notifications: section.notifications.map((notification) =>
            notification.notification_id === value.notification_id
              ? { ...notification, is_working: value.is_working }
              : notification
          ),
        };
      });
    },

    SET_PUSH_OFFER(state, value) {
      // waiting for backend to supporting this settings
      state.push_offer = value;
    },

    SET_PUSH_AVATAR(state, value) {
      // waiting for backend to supporting this settings
      state.push_avatar = value;
    },

    SET_PUSH_REST(state, value) {
      // waiting for backend to supporting this settings
      state.push_rest = value;
    },

    SET_PUSH_EVENT_TYPES_ARE_LOADING(state, value) {
      state.push_event_types_are_loading = value;
    },

    RESET_STATE(state) {
      Object.assign(state, resetState());
    },
  },

  actions: {
    /**
     * Получаем пользователя из api manager
     */
    async fetchUser(ctx, data) {
      return await UserService.fetchUser(data);
    },

    async loadPushEventTypes({ commit, dispatch }) {
      commit("SET_PUSH_EVENT_TYPES_ARE_LOADING", true);

      let settings;

      try {
        settings = await UserService.fetchPushSettings();
      } finally {
        commit("SET_PUSH_EVENT_TYPES_ARE_LOADING", false);
      }

      dispatch("initPushSettings", settings);
    },

    async changeField({ commit, dispatch }, { fieldName, value, endSessions }) {
      commit("SET_TEXT_FIELD_IS_LOADING", true);
      let updatedUser;

      try {
        updatedUser = await UserService.changeUserSettingsField(fieldName, value, endSessions);
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error(`Ошибка при загрузке поля. ${fieldName}`);
        }
      } finally {
        commit("SET_TEXT_FIELD_IS_LOADING", false);
      }

      dispatch("changeTextField", { fieldName, value });

      return updatedUser;
    },

    async changeAvatar({ commit, dispatch }, file) {
      let userData;

      commit("SET_AVATAR_IS_LOADING", true);

      try {
        userData = await UserService.changeUserSettingsField("avatar", file);
      } catch {
        throw new Error("Ошибка при загрузке аватарки.");
      } finally {
        commit("SET_AVATAR_IS_LOADING", false);
      }

      dispatch("changeAvatarURL", userData?.avatar?.thumb?.url);
    },

    changeAvatarURL({ commit }, url) {
      commit("User/SET_AVATAR_URL", url, { root: true });
    },

    changeTextField({ commit }, { fieldName, value }) {
      switch (fieldName) {
        case "name":
          commit("User/SET_NAME", value, { root: true });
          break;

        case "position":
          commit("User/SET_POSITION", value, { root: true });
          break;

        case "email":
          // commit("User/SET_EMAIL", value, { root: true });

          // сейчас email меняем через подтверждение
          break;

        case "phone":
          commit("User/SET_PHONE", value, { root: true });
          break;

        default:
          break;
      }
    },

    async changePassword({ commit }, { currentPassword, newPassword }) {
      commit("SET_PASSWORD_IS_UPDATING", true);

      try {
        await UserService.changePassword({ currentPassword, newPassword });
      } finally {
        commit("SET_PASSWORD_IS_UPDATING", false);
      }
    },

    async checkPassword({ commit }, password) {
      commit("SET_PASSWORD_IS_UPDATING", true);

      try {
        await UserService.checkPassword(password);
      } finally {
        commit("SET_PASSWORD_IS_UPDATING", false);
      }
    },

    async confirmChangeEmail(ctx, code) {
      await UserService.confirmChangeEmail(code);
    },

    async confirmUserBlock(ctx, code) {
      await UserService.confirmUserBlock(code);
    },

    initPushSettings({ commit }, settings) {
      for (let setting in settings) {
        commit(`SET_PUSH_${setting.toUpperCase()}`, settings[setting]);
      }
    },

    async togglePushSetting({ commit, getters }, key) {
      commit("SET_PUSH_EVENT_TYPES_ARE_LOADING", true);

      const commitName = `SET_PUSH_${key.toUpperCase()}`;

      const newValue = !getters["getPushSettingFor"](key);

      try {
        await UserService.setPushSetting(key, newValue);

        commit(commitName, newValue);
      } finally {
        commit("SET_PUSH_EVENT_TYPES_ARE_LOADING", false);
      }
    },

    async getNotify({ commit }, data) {
      try {
        const response = await UserService.fetchNotify(data);
        commit("SET_LIST_NOTIFY", response.data?.notifications);
      } catch (e) {
        console.error(e);
      }
    },

    async getSettingNotify({ commit }, data) {
      try {
        const response = await UserService.fetchSettingNotify(data);
        commit("SET_SETTING_NOTIFY", response.data);
      } catch (e) {
        Vue.notify({
          type: "error",
          text: e.message,
          duration: -1,
        });
        console.error(e);
      }
    },

    async changSetupNotify({ commit }, data) {
      try {
        await UserService.fetchSetupNotify(data);

        if (data.settings[0]?.notification_id) {
          commit("SET_ITEM_SETTING_NOTIFY", data.settings);
        }
      } catch (e) {
        Vue.notify({
          type: "error",
          text: e.message,
          duration: -1,
        });
        console.error(e);
      }
    },

    async changeSettingNotify({ commit }, data) {
      try {
        const response = await UserService.fetchChangeSettingNotify(data);
        if (!data.simple_settings) {
          commit("SET_SETTING_NOTIFY", {
            settings: response.data.settings,
            simple_settings: data.simple_settings,
          });
        } else {
          commit("SET_SETTING_NOTIFY", {
            settings: response.data.settings,
            simple_settings: data.simple_settings,
          });
        }
      } catch (e) {
        Vue.notify({
          type: "error",
          text: e.message,
          duration: -1,
        });
        console.error(e);
      }
    },

    resetState({ commit, dispatch }) {
      dispatch("Notifications/resetState");

      commit("RESET_STATE");
    },
  },

  modules: {
    Notifications,
  },
};
